/**
 * Lokala fakta per kommun – datatyp och textformatering.
 *
 * Datan byggs i localFactsData.ts (bara riktiga källor, se där) och skickas
 * med route loaderns data, så att blocket "Lokala fakta för <kommun>" och
 * metabeskrivningen finns i serverns HTML. Den här modulen är liten och fri
 * från kommundatan, så den kan användas i head() utan att tynga huvudpaketet.
 */

export type LocalFactsArea = "SE1" | "SE2" | "SE3" | "SE4";

export interface LocalFacts {
  slug: string;
  /** Kommunnamn i grundform, t.ex. "Ale". */
  name: string;
  /** Län utan "län", t.ex. "Västra Götalands". */
  county: string;
  /** Elområde (verifierat postnummeruppslag i centralorten, annars länets). */
  area: LocalFactsArea;
  /** Postnumret i centralorten som elområdet slogs upp på, eller null (länets elområde). */
  areaPostalCode: string | null;
  /** Folkmängd (Wikidata/SCB, src/data/municipalityFacts.ts). */
  population: number | null;
  /** Landareal i km². */
  landArea: number | null;
  /** Invånare per km² (folkmängd / landareal). */
  density: number | null;
  /** Plats i länet efter folkmängd (1 = störst) och antal kommuner i länet. */
  countyRank: { rank: number; of: number } | null;
  /** Nätägare och elnätsområde för ett postnummer i centralorten. */
  grid: { owner: string; gridArea: string; postalCode: string } | null;
  /** Snittspotpris förra hela månaden i elområdet (öre/kWh exkl. moms). */
  monthPrice: { year: number; month: number; avg: number } | null;
  /** Dagens snittspotpris i elområdet (öre/kWh exkl. moms). */
  todayPrice: { date: string; avg: number } | null;
}

const MONTHS = [
  "januari", "februari", "mars", "april", "maj", "juni",
  "juli", "augusti", "september", "oktober", "november", "december",
];

export const monthName = (month: number) => MONTHS[month - 1] ?? "";

const nf0 = new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 1 });

/** Heltal med svensk tusentalsavgränsning. */
export const formatInt = (v: number) => nf0.format(v);

/** Spotpris i öre/kWh: heltal från 10 öre, annars en decimal (SE1/SE2 kan ligga nära noll). */
export const formatOre = (v: number) => (Math.abs(v) >= 10 ? nf0.format(v) : nf1.format(v));

/** "3 november 2026" ur YYYY-MM-DD. */
export function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${monthName(m)} ${y}`;
}

/**
 * Nätägarens namn utan bolagsform ("Ale Elförening ek för" → "Ale Elförening"),
 * för löptext och metabeskrivningar.
 */
export function shortOwnerName(owner: string): string {
  return owner
    .replace(/\s+\(publ\)$/i, "")
    .replace(/\s+(ek\.?\s*för\.?|ekonomisk förening|AB)$/i, "")
    .trim();
}

/**
 * Unik metabeskrivning för en kommunsida: rubriken följd av kommunens egna
 * siffror och en kort uppmaning. Siffrorna kommer först så att de överlever
 * avkortningen till ~155 tecken. Saknas en uppgift utelämnas den.
 */
export function localFactsDescription(title: string, f: LocalFacts, cta: string): string {
  const facts: string[] = [];
  if (f.population != null) facts.push(`${formatInt(f.population)} invånare`);
  facts.push(`elområde ${f.area}`);
  if (f.monthPrice) {
    facts.push(`snittspotpris i ${monthName(f.monthPrice.month)} ${formatOre(f.monthPrice.avg)} öre/kWh`);
  }
  const grid = f.grid ? ` Elnät: ${shortOwnerName(f.grid.owner)}.` : "";
  return `${title} – ${facts.join(", ")}.${grid} ${cta}`.trim();
}

/** Ett faktum i blocket: etikett, värde och källa/förklaring. */
export interface LocalFactRow {
  label: string;
  value: string;
  note: string;
}

/** Raderna i "Lokala fakta för <kommun>", i visningsordning. Bara uppgifter som finns. */
export function localFactRows(f: LocalFacts): LocalFactRow[] {
  const rows: LocalFactRow[] = [
    {
      label: "Elområde",
      value: f.area,
      note: f.areaPostalCode
        ? `Uppslaget på postnummer ${f.areaPostalCode} i centralorten`
        : `Elområde för ${f.county} län`,
    },
  ];
  if (f.monthPrice) {
    rows.push({
      label: `Spotpris ${monthName(f.monthPrice.month)} ${f.monthPrice.year}`,
      value: `${formatOre(f.monthPrice.avg)} öre/kWh`,
      note: `Månadens snitt i ${f.area}, exkl. moms (Nord Pool)`,
    });
  }
  if (f.todayPrice) {
    rows.push({
      label: "Senaste dygnssnitt",
      value: `${formatOre(f.todayPrice.avg)} öre/kWh`,
      note: `Spotpris ${formatIsoDate(f.todayPrice.date)} i ${f.area}, exkl. moms`,
    });
  }
  if (f.grid) {
    rows.push({
      label: "Elnätsbolag",
      value: f.grid.owner,
      note: `Nätområde ${f.grid.gridArea} (postnummer ${f.grid.postalCode}) – kan variera inom kommunen`,
    });
  }
  if (f.population != null) {
    rows.push({
      label: "Invånare",
      value: formatInt(f.population),
      note: f.countyRank
        ? `${f.countyRank.rank === 1 ? "Störst" : `Nummer ${f.countyRank.rank}`} av ${f.countyRank.of} kommuner i ${f.county} län`
        : `${f.county} län`,
    });
  }
  if (f.landArea != null) {
    rows.push({
      label: "Landareal",
      value: `${formatInt(f.landArea)} km²`,
      note: f.density != null ? `${nf1.format(f.density)} invånare per km²` : "",
    });
  }
  return rows;
}

/** Loaderdatans nyckel för lokala fakta. */
export const LOCAL_FACTS_KEY = "localFacts";
